import {
  AssetInstance,
  Requisition,
  RequisitionFulfillmentItem,
  RequisitionItem,
  User,
} from '@kalos/kalos-rpc';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { addYears, format } from 'date-fns';

import { queryKeys } from '../../../hooks/react-query/constants';
import {
  EmailClientService,
  FileClientService,
  InventoryClientService,
  UserClientService,
} from '../../../tools/helpers';
import { getRequisitionStatusEmail } from '../../ComponentsLibrary/ToolFund/components/useRequisitionStatusUpdate';
import {
  type AdminToolPurchaseFormData,
  type AdminToolPurchaseLineData,
  APPROVED_STATUS_ID,
  fulfillmentsInLineOrder,
  PENDING_STATUS_ID,
  type PurchaseLineFiles,
  receivedFulfillmentIds,
  toLineApproval,
  TOOL_FUND_REQUISITION_TYPE_ID,
  toPurchaseItems,
} from './adminToolPurchase';

const TOOL_FILES_BUCKET = 'kalos-tools-files';

/** Uploads a line's receipt and photo with the request page's keys, so they're listed the same. */
const uploadLineFiles = async (
  userId: number,
  line: Pick<AdminToolPurchaseLineData, 'receipt' | 'photo'>,
): Promise<PurchaseLineFiles> => {
  const upload = async (file: File, kind: 'receipt' | 'photo') =>
    (
      await FileClientService.uploadFileToS3AndCreateFile({
        file,
        bucket: TOOL_FILES_BUCKET,
        fileKey: `${userId}_${kind}_${Date.now()}_${file.name}`,
      })
    ).id;
  if (!line.receipt) throw new Error('a receipt is required');
  const [receiptId, photoId] = await Promise.all([
    upload(line.receipt, 'receipt'),
    line.photo ? upload(line.photo, 'photo') : Promise.resolve(undefined),
  ]);
  return { receiptId, photoId };
};

/** The approval email the employee gets for each approved line, sent once per tool. */
const sendApprovedEmails = async (userId: number, lines: AdminToolPurchaseLineData[]) => {
  const { email } = await UserClientService.Get(User.create({ id: userId }));
  if (!email) throw new Error('the employee has no email address');
  await Promise.all(
    lines.map((line) =>
      EmailClientService.sendMail({
        subject: 'Tool Fund Request Approved',
        body: getRequisitionStatusEmail({
          statusId: APPROVED_STATUS_ID,
          purchaseOrder: '',
          rejectReason: '',
          purchaseDate: format(line.purchaseDate, 'yyyy-MM-dd'),
        }),
        recipient: email,
      }),
    ),
  );
};

/** Marks a tool received the way the Tool Log's mark-received button does. */
const markFulfillmentReceived = async (fulfillmentId: number) => {
  const { results } = await InventoryClientService.BatchGetAssetInstance(
    AssetInstance.create({ fulfillmentId }),
  );
  if (!results[0]) throw new Error(`no asset for fulfillment ${fulfillmentId}`);
  const now = new Date();
  await InventoryClientService.UpdateAssetInstance(
    AssetInstance.create({
      id: results[0].id,
      acquisitionDate: format(now, 'yyyy-MM-dd HH:mm:ss'),
      vestingDate: format(addYears(now, 3), 'yyyy-MM-dd HH:mm:ss'),
      fieldMask: ['AcquisitionDate', 'VestingDate'],
    }),
  );
};

/**
 * Records a purchase an admin made for the employee the way an approved request comes about:
 * upload each tool's receipt and photo, create the tool fund request charged to `payerId`, approve
 * each line as the approval screen does (the server writes the asset and the deduction on the
 * purchase date), and mark the request complete. Then it marks the tools they already have as
 * received and emails the employee. A failed receive or email doesn't undo the purchase; the form
 * says so. If approving fails part way, the error names the request to finish from the requests
 * page.
 */
export const useAdminToolPurchaseMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      payerId,
      approvedBy,
      data,
    }: {
      userId: number;
      payerId: number;
      approvedBy: number;
      data: AdminToolPurchaseFormData;
    }) => {
      const files = await Promise.all(data.lines.map((line) => uploadLineFiles(userId, line)));
      const now = new Date();
      const requisition = await InventoryClientService.CreateRequisition(
        Requisition.create({
          userId,
          chargedToEmpId: payerId,
          statusId: PENDING_STATUS_ID,
          requisitionTypeId: TOOL_FUND_REQUISITION_TYPE_ID,
          requestDate: format(now, 'yyyy-MM-dd HH:mm:ss'),
          fileIds: files.map((f) => f.receiptId).join(','),
          items: toPurchaseItems(data).map((item) => RequisitionItem.create(item)),
        }),
      );

      let fulfillments: RequisitionFulfillmentItem[];
      try {
        const { results } = await InventoryClientService.BatchGetRequisitionFulfillmentItem(
          RequisitionFulfillmentItem.create({ requisitionId: requisition.id }),
        );
        fulfillments = fulfillmentsInLineOrder(results);
        if (fulfillments.length !== data.lines.length) {
          throw new Error(`expected ${data.lines.length} lines, found ${fulfillments.length}`);
        }
        for (const [i, fulfillment] of fulfillments.entries()) {
          await InventoryClientService.UpdateRequistionFulfillmentItem(
            RequisitionFulfillmentItem.create({
              ...fulfillment,
              ...toLineApproval(data.lines[i], files[i], approvedBy, now),
            }),
          );
        }
        await InventoryClientService.UpdateRequisition(
          Requisition.create({
            id: requisition.id,
            statusId: APPROVED_STATUS_ID,
            updatedAt: format(now, 'yyyy-MM-dd HH:mm:ss'),
            fieldMask: ['StatusId'],
          }),
        );
      } catch (error) {
        const reason = error instanceof Error ? error.message : String(error);
        throw new Error(
          `Request #${requisition.id} was created but not fully approved. Finish approving it from Tool Fund Requests. (${reason})`,
        );
      }

      const [received, emailed] = await Promise.all([
        Promise.allSettled(
          receivedFulfillmentIds(data.lines, fulfillments).map(markFulfillmentReceived),
        ),
        sendApprovedEmails(userId, data.lines).then(
          () => true,
          () => false,
        ),
      ]);
      return {
        requisition,
        receiveFailures: received.filter((r) => r.status === 'rejected').length,
        emailFailed: !emailed,
      };
    },
    onSettled: async () => {
      await Promise.allSettled([
        queryClient.invalidateQueries({ queryKey: [queryKeys.toolFundTransaction.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.toolFundBalance.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.requisition.root] }),
        queryClient.invalidateQueries({ queryKey: [queryKeys.assetInstance.root] }),
      ]);
    },
  });
};
